class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.products = this.dataset.products
      ? JSON.parse(this.dataset.products)
      : [];
    this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
    this.selected = new Map();
    this.cartPayload = { items: [] };

    this.chipsBottom =
      this.querySelector('[data-role="chips-bottom"]') ||
      document.createElement("div");
    this.totalEl =
      this.querySelector('[data-role="total"]') ||
      document.createElement("span");
    this.checkoutBtn =
      this.querySelector('[data-role="checkout"]') ||
      document.createElement("button");

    this.quantities = new Map();

    this.addEventListener("click", (e) => {
      const qtyWrap = e.target.closest('[data-role="qty"]');
      if (qtyWrap) {
        const pid = Number(qtyWrap.getAttribute("data-id"));
        const span = qtyWrap.querySelector(".quantity-js");
        const isMinus = e.target.closest(".quantity-minus-js");
        const isPlus = e.target.closest(".quantity-plus-js");
        if (span && (isMinus || isPlus)) {
          const curr = this.quantities.get(pid) || 1;
          const next = Math.max(1, curr + (isPlus ? 1 : -1));
          this.quantities.set(pid, next);
          span.textContent = String(next);
        }
        return;
      }

      const addBtn = e.target.closest('[data-role="add"][data-add-id]');
      if (addBtn) {
        const slide = addBtn.closest(".swiper-slide");
        const productId = Number(addBtn.getAttribute("data-add-id"));
        const productMeta = this.products.find((x) => x.id === productId);

        if (!productMeta || !productMeta.firstAvailableVariantId) return;

        const variantId = Number(productMeta.firstAvailableVariantId);
        const variantBasePrice = Number(productMeta.price || 0);
        const variantTitle = productMeta.title;
        const productImage = productMeta.image;

        const unitPrice = variantBasePrice;
        const qtyToAdd = this.quantities.get(productId) || 1;

        const key = String(variantId);
        const existed = this.selected.get(key);
        if (existed) {
          this.selected.delete(key);
          existed.quantity += qtyToAdd;
          this.selected.set(key, existed);
        } else {
          this.selected.set(key, {
            productId,
            variantId,
            title: variantTitle,
            price: unitPrice,
            image: productImage,
            quantity: qtyToAdd,
          });
        }

        this.cartPayload.items = Array.from(this.selected.values()).map(
          (x) => ({ id: x.variantId, quantity: x.quantity })
        );

        this.update();

        requestAnimationFrame(() => {
          const sc = this.querySelector("site-carousel");
          if (sc && typeof sc.next === "function") sc.next();
        });

        return;
      }

      const removeBtn = e.target.closest("[data-remove]");
      if (removeBtn) {
        const rid = removeBtn.getAttribute("data-remove");
        this.selected.delete(rid);
        this.cartPayload.items = Array.from(this.selected.values()).map(
          (x) => ({ id: x.variantId, quantity: x.quantity })
        );
        this.update();
        return;
      }
    });

    this.checkoutBtn.addEventListener("click", async () => {
      if (this.selected.size === 0) {
        this.scrollToCarouselTop();
        return;
      }

      const original = this.checkoutBtn.textContent;
      this.checkoutBtn.textContent = "Adding...";
      this.checkoutBtn.disabled = true;
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: this.cartPayload.items }),
        });

        // TVSquared action tracking
        if (window.tvqTrackAction) {
          var totalPrice = 0;
          this.selected.forEach(function(item) {
            var itemPrice = (parseInt(item.price, 10) || 0) * item.quantity;
            totalPrice += itemPrice;
            window.tvqTrackAction('add_to_cart', {
              'rev': '0', 'prod': item.title || '', 'id': String(item.variantId), 'promo': ''
            });
          });
          window.tvqTrackAction('checkout_started', {
            'rev': String(totalPrice / 100), 'prod': 'Bundle', 'id': '', 'promo': ''
          });
        }

        window.location.href = "/checkout";
      } catch (e) {
        this.checkoutBtn.textContent = original;
        this.checkoutBtn.disabled = false;
      }
    });

    this.update();
  }

  scrollToCarouselTop() {
    const target =
      this.querySelector("site-carousel") ||
      this.querySelector(".swiper") ||
      this;
    const rect = target.getBoundingClientRect();
    const y = Math.max(0, rect.top + window.scrollY - 190);
    window.scrollTo({ top: y, behavior: "smooth" });
  }

  updateStepStatuses() {
    const selectedProductIds = new Set(
      Array.from(this.selected.values()).map((x) => x.productId)
    );

    this.querySelectorAll('[data-role="step-status"][data-id]').forEach(
      (el) => {
        const pid = Number(el.getAttribute("data-id"));
        const added = selectedProductIds.has(pid);
        el.textContent = added ? "Added" : "Not added";
      }
    );
  }

  update() {
    const lines = Array.from(this.selected.entries()).reverse();
    const chipsHTML = lines
      .map(
        ([key, x]) => `
      <button class="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm shrink-0" data-remove="${key}">
        <img src="${this.escape(
          x.image || ""
        )}" alt="" class="rounded w-[30px] h-[30px] object-cover"/>
        <span class="truncate max-w-[140px]">${this.escape(x.title)}</span>
        <span class="opacity-60">(${x.quantity})</span>
        <span class="opacity-60">×</span>
      </button>
    `
      )
      .join("");
    this.chipsBottom.innerHTML = chipsHTML;

    const total = Array.from(this.selected.values()).reduce(
      (s, x) => s + (x.price || 0) * (x.quantity || 1),
      0
    );
    const formatted = this.formatMoney(total);
    this.totalEl.textContent = formatted;

    if (this.selected.size > 0) {
      this.checkoutBtn.innerHTML = `Buy Now — <span class="ml-1">${formatted}</span>`;
    }

    this.updateStepStatuses();
  }

  // Formats cents with the storefront's money format, which Shopify Markets
  // localizes per country — so it can use any of Shopify's amount tokens, not
  // just {{amount}}. Handling only a few left the others on the page verbatim
  // (Sweden's "{{amount_with_space_separator}} kr" rendered as-is). Each
  // token's separators are fixed, as in Shopify's own Liquid money filters,
  // rather than taken from the browser's locale.
  formatMoney(cents) {
    const format = (decimals, thousands, decimal) => {
      const [whole, fraction] = ((cents || 0) / 100).toFixed(decimals).split(".");
      return (
        whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
        (fraction ? decimal + fraction : "")
      );
    };
    const tokens = {
      amount: () => format(2, ",", "."),
      amount_no_decimals: () => format(0, ",", "."),
      amount_with_comma_separator: () => format(2, ".", ","),
      amount_no_decimals_with_comma_separator: () => format(0, ".", ","),
      amount_with_apostrophe_separator: () => format(2, "'", "."),
      amount_no_decimals_with_space_separator: () => format(0, " ", ","),
      amount_with_space_separator: () => format(2, " ", ","),
      amount_with_period_and_space_separator: () => format(2, " ", "."),
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) =>
      (tokens[token] || tokens.amount)()
    );
  }

  escape(s) {
    return (s ?? "").toString().replace(
      /[&<>"']/g,
      (m) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[m])
    );
  }
}
customElements.define("bundle-builder", BundleBuilder);
